import { useHandleMetadata } from 'hooks/use_handle_metadata'
import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import { useSelector } from 'react-redux'
import { makeGetContact } from 'reduxes/slices/contacts/selectors'

import { css } from 'design'
import { Button } from 'primitives/button'
import * as Popover from 'primitives/popover'
import { cn } from 'util/cn'

import { useNavToDirectChat } from '../../hooks/app_location/use_nav_to_direct_chat'
import { useNavToTopic } from '../../hooks/app_location/use_nav_to_topic'
import {
  getCurrentAccount,
  getCurrentHandle,
} from '../../reduxes/slices/current/selectors'
import {isLiveChatApp} from "../../reduxes/slices/live_chat_app/selectors";
import { makeGetTopicById } from '../../reduxes/slices/topics/selectors'
import {useSelectorDelegateStore} from "../../reduxes/stores/root_store/hooks";
import { TimelineType } from '../../types/models'
import { GROUP_TOPIC_TYPES } from "../../util/constants";
import { Timestamp } from '../timekeeping/timestamp'
import ActionsBar from './actions/actions_bar'
import EmojiReactions from './actions/emoji_reactions'
import { TimelineAvatar } from './timeline_avatar'
import { TimelineContent } from './timeline_content'
import TimelineStatus from './timeline_status'
import { TimelinesFocusContext } from './timelines_focus_manager'
import {makeGetDirectChatTopicFromHandle} from "../../reduxes/slices/direct_chats/selectors";

export type TimelineProps = {
  timeline: TimelineType
  item: any
  handle: string | null
  children?: React.ReactNode
  withActionsBar?: boolean
  withEmojiReactions?: boolean
  withTopicSubject?: boolean
  withTimelineInfo?: boolean
  withLink?: boolean
  avatarType?: 'popup' | 'readonly' | 'spacer' | 'none'
  avatar?: React.ReactElement
  threadedFrom?: boolean
} & React.HTMLAttributes<HTMLDivElement>

export default function Timeline({
  timeline,
  item,
  handle,
  children,
  avatarType = 'popup',
  avatar,
  withActionsBar = true,
  withEmojiReactions = true,
  withTopicSubject = false,
  withTimelineInfo = true,
  withLink = false,
  threadedFrom = false,
  className,
  ...props
}: TimelineProps) {
  const {
    topic_id: topicId,
    edited_at: editedAt,
    created_at: createdAt,
  } = timeline
  const [showActionsBar, setShowActionsBar] = useState(false)
  const [focus, setFocus] = useState(false)
  const account = useSelector(getCurrentAccount)
  const liveChatApp = useSelectorDelegateStore(isLiveChatApp)

  const { focusLock } = useContext(TimelinesFocusContext)

  const timelineContainerRef = useRef<HTMLDivElement>(null)

  const isDeleted = !!item?.deleted_at
  const timelineId = timeline?.id

  useEffect(() => {
    const timelineContainer = timelineContainerRef.current
    if (!timelineContainer || focusLock) {
      return
    }
    const hoverClass = liveChatApp
      ? 'timeline--hover-widget'
      : 'timeline--hover'
    if (focus) {
      if (withActionsBar) {
        setShowActionsBar(true)
      }

      timelineContainer.classList.add(hoverClass)
    } else {
      if (withActionsBar) {
        setShowActionsBar(false)
      }

      timelineContainer.classList.remove(hoverClass)
    }
  }, [focus, focusLock, liveChatApp, timelineId, withActionsBar])

  const getTopic = useMemo(makeGetTopicById, [])
  const topic = useSelector((state) => getTopic(state, topicId))

  const currentHandle = useSelector(getCurrentHandle)
  const directChatHandle = topic?.contact_handles?.find((x: string) => {
    if (topic?.participant_mode === 'direct_chat') {
      return x !== currentHandle
    } else if (topic?.participant_mode === 'personal') {
      return x === currentHandle
    }
  })
  const { name } = useHandleMetadata(handle || undefined)
  const { name: directChatName } = useHandleMetadata(directChatHandle)
  const threadTopic = useSelector((state) =>
    getTopic(state, timeline?.thread_topic_id),
  )
  const navToTopic = useNavToTopic()

  const getContact = useMemo(makeGetContact, [])
  const contact = useSelector((state) => getContact(state, { handle })) || {
    handle,
  }

  const getDirectChatTopicFromHandle = useMemo(
    makeGetDirectChatTopicFromHandle,
    [],
  )

  const navToDirectChat = useNavToDirectChat()

  const directChatTopic = useSelector((state: any) =>
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    getDirectChatTopicFromHandle(state, { handle }),
  )

  const navToContact = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault()
      e.stopPropagation()

      if (handle) {
        navToDirectChat(handle, directChatTopic?.id)
      }
    },
    [handle, navToDirectChat, directChatTopic?.id],
  )

  if (!timeline) {
    return <div />
  }

  let topicSubject
  if (
    topic?.participant_mode === 'direct_chat' ||
    topic?.participant_mode === 'personal'
  ) {
    topicSubject = directChatName && '@' + directChatName
  } else {
    topicSubject = topic?.subject
  }

  const navToThread = () => {
    navToTopic(threadTopic.id)
  }

  const repliesCount = threadTopic?.chats_count
  const noOfRepliesPhrase =
    repliesCount === 1 ? '1 reply' : `${repliesCount || 'No'} replies`

  const edited =
    editedAt &&
    new Date(editedAt).getTime() - new Date(createdAt).getTime() > 1000 * 60

  return (
    <Wrapper
      className={cn(
        'timeline',
        `${timeline.ref_name}-timeline`,
        isDeleted && deletedTimelineStyles(),
        item?.handle == currentHandle &&
          selfTimelineStyles({liveChatApp}),
        className,
      )}
      withLink={withLink}
      topic={topic}
      handle={directChatHandle}
      data-timelinedeleted={isDeleted}
      {...props}
      ref={timelineContainerRef}
      data-timeline={timeline.id}
      setFocus={setFocus}
    >
      <TimelineContent
        className={cn('main-row')}
        avatar={
          avatarType === 'popup' ? (
            liveChatApp ? (
              <TimelineAvatar
                avatarType={avatarType}
                avatar={avatar}
                handle={contact?.display_email || handle}
              />
            ) : (
              <Popover.Root>
                <Popover.Trigger className={triggerStyles()}>
                  <TimelineAvatar
                    avatarType={avatarType}
                    avatar={avatar}
                    handle={contact?.display_email || handle}
                  />
                </Popover.Trigger>
              </Popover.Root>
            )
          ) : (
            <TimelineAvatar
              avatarType={avatarType}
              avatar={avatar}
              handle={handle}
            />
          )
        }
        timelineInfo={
          withTimelineInfo && (
            <div className={`timeline-info`}>
              {avatarType === 'popup' ? (
                liveChatApp ? (
                  <b className={'timeline-info__handle'}>
                    {item?.handle === currentHandle &&
                    account?.plan === 'widget'
                      ? 'You'
                      : name}
                  </b>
                ) : (
                    <Button variant={'link'} className={triggerStyles()} onClick={navToContact}>
                      <b className={'timeline-info__handle'}>{name}</b>
                    </Button>
                )
              ) : (
                <b className={'timeline-info__handle'}>
                  {item?.handle === currentHandle && account?.plan === 'widget'
                    ? 'You'
                    : name}
                </b>
              )}

              <Timestamp
                date={createdAt}
                className="timeline-info__timestamp"
              />
              {item &&
                item.read_status &&
                item.handle &&
                item.handle == currentHandle && (
                  <span className={cn(readStatusStyles())}>
                    <ReadStatus readStatus={item.read_status} />
                  </span>
                )}
              {timeline &&
                timeline.handle &&
                timeline.handle === currentHandle && (
                  <TimelineStatus timeline={timeline} />
                )}
              {edited && (
                <span className={cn(timelineEditedStyles())}>(edited)</span>
              )}
              {withTopicSubject && topicSubject && (
                <span className={cn('topic-subject', topicSubjectStyles())}>
                  {topicSubject}
                </span>
              )}
            </div>
          )
        }
      >
        {children}
        {withEmojiReactions && <EmojiReactions timeline={timeline} />}
        {threadTopic && !threadedFrom && (
          <Button
            className={'nav-to-thread-btn'}
            variant={'blank'}
            onClick={navToThread}
          >
            {noOfRepliesPhrase}
            {threadTopic.last_message && (
              <span className={'date-small text-muted'}>
                <span>&nbsp;&nbsp;Last reply&nbsp;</span>
                <Timestamp date={threadTopic.last_message} />
              </span>
            )}
            <span className={'hover-text'}>
              &nbsp;&nbsp;|&nbsp;&nbsp;View Topic
            </span>
          </Button>
        )}
      </TimelineContent>
      {threadedFrom && (
        <div className={'reply-count'}>
          {noOfRepliesPhrase}
          <div className={'divider'} />
          <Button
            onClick={() => {
              if (topicId) {
                navToTopic(topicId)
              }
            }}
          >
            View this message in {topic?.subject}
          </Button>
        </div>
      )}
      {showActionsBar && !isDeleted && (
        <ActionsBar timeline={timeline} item={item} />
      )}
    </Wrapper>
  )
}

type TimelineWrapperProps = {
  withLink: boolean
  topic: any
  handle?: string
  setFocus: React.Dispatch<React.SetStateAction<boolean>>
} & React.HTMLAttributes<HTMLDivElement | HTMLButtonElement>

const Wrapper = React.forwardRef<HTMLDivElement, TimelineWrapperProps>(
  ({ withLink, children, topic, onClick, handle, setFocus, ...props }, ref) => {
    const navToTopic = useNavToTopic()
    const navToDirectChat = useNavToDirectChat()

    const wrapperHandlers = useMemo(() => {
      const handlers: any = {
        onMouseOver: () => setFocus(true),
        onMouseLeave: () => setFocus(false),
        onClick: (e: any) => {
          if (e.target.getAttribute('data-releasefocus')) {
            e.preventDefault()
            e.stopPropagation()
            setFocus(false)
          }
        },
      }

      return handlers
    }, [setFocus])

    const handleClick = (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
      if (GROUP_TOPIC_TYPES.includes(topic?.participant_mode)) {
        navToTopic(topic.id)
      } else {
        navToDirectChat(handle, topic.id)
      }
      if (onClick) {
        onClick(event)
      }
    }

    return (
      <div {...props} {...wrapperHandlers} ref={ref}>
        {withLink ? (
          <Button onClick={handleClick}>{children}</Button>
        ) : (
          children
        )}
      </div>
    )
  },
)

const triggerStyles = css({
  border: 'none',
  background: 'none',
  padding: 0,
  display: 'flex',
  maxWidth: '70%',
  cursor: 'pointer',
  textDecoration: 'none !important',

  '@hover': {
    '&:hover': {
      '> *': {
        textDecoration: 'underline',
        textAlign: 'left',
      },
    },
  },
})

const profileContentStyles = css({
  position: 'absolute',
  left: '0 !important',

  '@sm': {
    position: 'initial',
    left: 'initial',
  },
})

const topicSubjectStyles = css({
  marginLeft: '$2',
})

const timelineEditedStyles = css({
  marginLeft: '$2',
  fontSize: '10px',
  color: '$grey500',
})

const readStatusStyles = css({
  fontSize: '8px',
  marginLeft: '$2',
  letterSpacing: '-1px',
  color: '$grey500',
})

const readStatusGreenStyles = css({
  color: '$green500',
})

const deletedTimelineStyles = css({
  '.chat': {
    opacity: 0.5,
  },
})

const selfTimelineStyles = css({
  variants: {
    liveChatApp: {
      true: {
        background: 'none',
      },
      false: {
        background: '$grey25',
      },
    },
  },
})

const ReadStatus = ({ readStatus }: { readStatus: string }) => {
  switch (readStatus) {
    case 'created':
      return <span>✓</span>
    case 'notified':
      return <span>✓✓</span>
    case 'read':
      return <span className={cn(readStatusGreenStyles())}>✓✓</span>
    default:
      return null
  }
}

Wrapper.displayName = 'TimelineWrapper'
